import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { baseUrl, withWsToken } from '../app/api/apiSlice';
import { useFetchChatSessionByIdQuery } from './chat_sessions/chatSessionApiSlice';
import CameraFeed from './chat_sessions/CameraFeed';
import AudioOrbVisual from './visuals/AudioOrbVisual';
import {
  Header,
  Button,
  Icon,
  Message,
  Segment,
} from 'semantic-ui-react';
import { useBreakpoint } from '../hooks/useBreakpoint';
import AICoachingPanel from './visuals/AICoachingPanel';

const AudioOrb = () => {
  const { isMobile } = useBreakpoint();
  const [searchParams] = useSearchParams();
  const [isConnected, setIsConnected] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [error, setError] = useState(null);
  const [volume, setVolume] = useState(0);
  const sessionId = searchParams.get('session_id');
  const [transcript, setTranscript] = useState('');
  const [aiResponse, setAiResponse] = useState('');
  const [monitoringEnabled, setMonitoringEnabled] = useState(false);
  const [guideMessage, setGuideMessage] = useState('');
  const [customerInsights, setCustomerInsights] = useState([]);
  const [currentPhase, setCurrentPhase] = useState('');
  const [recommendedProduct, setRecommendedProduct] = useState('');
  const [productSpecs, setProductSpecs] = useState([]);
  const [pinnedProduct, setPinnedProduct] = useState(null); // { name: string, specs: [] }
  const [newSuggestion, setNewSuggestion] = useState(null); // For showing different product while pinned

  const { data: chatSession } = useFetchChatSessionByIdQuery(sessionId, {
    skip: !sessionId,
  });
  const currentPersona = chatSession?.persona;

  const wsRef = useRef(null);
  const audioContextRef = useRef(null);
  const processorRef = useRef(null);
  const sourceRef = useRef(null);
  const outputNodeRef = useRef(null);
  const audioQueueRef = useRef([]);
  const isPlayingRef = useRef(false);
  const nextStartTimeRef = useRef(0);
  const analyserRef = useRef(null);
  const animationFrameRef = useRef(null);
  const activeSourcesRef = useRef([]);
  const lastSpeakerRef = useRef(null); // 'user' | 'ai'
  const videoRef = useRef(null);
  const streamRef = useRef(null); // For audio stream
  const cameraStreamRef = useRef(null); // For video stream

  // Reconnection refs
  const isManualDisconnect = useRef(false);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const MAX_RECONNECT_ATTEMPTS = 5;

  useEffect(() => {
    const initCamera = async () => {
      try {
        if (navigator.mediaDevices) {
          const stream = await navigator.mediaDevices.getUserMedia({
            video: {
              width: { ideal: 320 },
              height: { ideal: 240 },
              facingMode: 'user',
            },
          });
          cameraStreamRef.current = stream;
          if (videoRef.current) {
            videoRef.current.srcObject = stream;
          }
        }
      } catch (err) {
        console.error('Error accessing camera:', err);
      }
    };

    initCamera();

    return () => {
      isManualDisconnect.current = true; // Ensure we don't reconnect on unmount
      disconnect();
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (cameraStreamRef.current) {
        cameraStreamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  const connect = () => {
    if (!sessionId) {
      setError('No chat session ID provided.');
      return;
    }
    setError(null);
    isManualDisconnect.current = false;

    // Derive WS URL from API base URL
    const wsProtocol = baseUrl.startsWith('https') ? 'wss' : 'ws';
    const wsHost = baseUrl.replace(/^https?:\/\//, '');
    const wsUrl = withWsToken(`${wsProtocol}://${wsHost}/ws/api/gemini-live/${sessionId}/`);

    const ws = new WebSocket(wsUrl);
    ws.binaryType = 'arraybuffer';

    ws.onopen = () => {
      console.log('Connected to Customer Simulation Live');
      setIsConnected(true);
      reconnectAttemptsRef.current = 0; // Reset attempts on success
      startRecording();

      // Send initial monitoring state
      ws.send(
        JSON.stringify({
          type: 'toggle_monitoring',
          enabled: monitoringEnabled,
        }),
      );
    };

    ws.onmessage = async event => {
      if (event.data instanceof ArrayBuffer) {
        handleAudioData(event.data);
      } else if (typeof event.data === 'string') {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'user_transcript') {
            if (lastSpeakerRef.current !== 'user') {
              setTranscript('');
              lastSpeakerRef.current = 'user';
            }
            setTranscript(prev => (prev + ' ' + data.text).trim());
          } else if (data.type === 'ai_transcript') {
            if (lastSpeakerRef.current !== 'ai') {
              setAiResponse('');
              lastSpeakerRef.current = 'ai';
            }
            setAiResponse(prev => (prev + ' ' + data.text).trim());
          } else if (data.type === 'interrupted') {
            console.log('AI interrupted, clearing audio queue');
            clearAudioQueue();
            setAiResponse(prev => prev + ' [Interrupted]');
            // If interrupted, it means user is speaking, so next user transcript will handle the switch if not already done
          } else if (data.type === 'turn_complete') {
            // AI finished turn
          } else if (data.type === 'guide_message') {
            setGuideMessage(data.text);
          } else if (data.type === 'guide_state') {
            const state = data.data;
            setCustomerInsights(state.customer_insights || []);
            setCurrentPhase(state.current_phase || '');

            const newProductName = state.recommended_product_name || '';
            const newProductSpecs = state.relevant_product_specs || [];

            // Handle product recommendation with pin logic
            if (newProductName) {
              setRecommendedProduct(newProductName);
              setProductSpecs(newProductSpecs);

              // If pinned and different product, show as new suggestion
              if (pinnedProduct && pinnedProduct.name !== newProductName) {
                setNewSuggestion({
                  name: newProductName,
                  specs: newProductSpecs,
                });
              } else {
                setNewSuggestion(null);
              }
            } else {
              // Empty product - keep pinned if exists, clear current
              if (!pinnedProduct) {
                setRecommendedProduct('');
                setProductSpecs([]);
              }
              setNewSuggestion(null);
            }
          }
        } catch (e) {
          console.error('Error parsing JSON message', e);
        }
      }
    };

    ws.onclose = event => {
      console.log('Disconnected from Customer Simulation Live', event.code);
      setIsConnected(false);
      stopRecording();

      if (isManualDisconnect.current) {
        // Expected disconnect
        return;
      }

      if (event.code === 4000) {
        setError('Missing API_KEY on backend.');
      } else if (reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS) {
        // Attempt reconnect
        const delay = Math.min(
          1000 * Math.pow(2, reconnectAttemptsRef.current),
          10000,
        );
        console.log(
          `Connection dropped (Code: ${event.code}). Reconnecting in ${delay}ms... (Attempt ${reconnectAttemptsRef.current + 1}/${MAX_RECONNECT_ATTEMPTS})`,
        );

        setError(
          `Connection dropped. Reconnecting... (${reconnectAttemptsRef.current + 1})`,
        );

        reconnectTimeoutRef.current = setTimeout(() => {
          reconnectAttemptsRef.current += 1;
          connect();
        }, delay);
      } else {
        setError('Connection failed. Please refresh or try again later.');
      }
    };

    ws.onerror = error => {
      console.error('WebSocket error:', error);
      // setError("WebSocket error occurred.");
    };

    wsRef.current = ws;
  };

  const disconnect = () => {
    isManualDisconnect.current = true;
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    stopRecording();
  };

  const startRecording = async () => {
    try {
      if (!navigator.mediaDevices) {
        throw new Error(
          'Microphone access requires a secure context (HTTPS) or localhost. If you are on mobile, please use HTTPS.',
        );
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate: 16000,
          channelCount: 1,
          echoCancellation: true,
          autoGainControl: true,
          noiseSuppression: true,
        },
      });

      streamRef.current = stream;

      // Create AudioContext if it doesn't exist
      // Explicitly set 16kHz to match backend WAV encoding and Gemini input requirement
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: 16000 });
      }

      // Create output node (GainNode) for visualizer
      if (!outputNodeRef.current) {
        outputNodeRef.current = audioContextRef.current.createGain();
        outputNodeRef.current.connect(audioContextRef.current.destination);
      }

      sourceRef.current =
        audioContextRef.current.createMediaStreamSource(stream);

      // Analyser for visualizer (Legacy, can be removed if we fully switch to AudioOrbVisual)
      analyserRef.current = audioContextRef.current.createAnalyser();
      analyserRef.current.fftSize = 256;
      sourceRef.current.connect(analyserRef.current);

      // Use ScriptProcessor for simplicity in this example, though AudioWorklet is preferred for production
      // We need to resample to 16000Hz for Gemini
      processorRef.current = audioContextRef.current.createScriptProcessor(
        4096,
        1,
        1,
      );

      const inputSampleRate = audioContextRef.current.sampleRate;
      const targetSampleRate = 16000;

      processorRef.current.onaudioprocess = e => {
        if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN)
          return;

        const inputData = e.inputBuffer.getChannelData(0);

        // Silence output to prevent feedback loop
        const outputData = e.outputBuffer.getChannelData(0);
        for (let i = 0; i < outputData.length; i++) {
          outputData[i] = 0;
        }

        // Simple downsampling if needed
        let pcmData;

        if (inputSampleRate === targetSampleRate) {
          pcmData = new Int16Array(inputData.length);
          for (let i = 0; i < inputData.length; i++) {
            const s = Math.max(-1, Math.min(1, inputData[i]));
            pcmData[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
          }
        } else {
          // Downsample
          const ratio = inputSampleRate / targetSampleRate;
          const newLength = Math.floor(inputData.length / ratio);
          pcmData = new Int16Array(newLength);

          for (let i = 0; i < newLength; i++) {
            const offset = Math.floor(i * ratio);
            const s = Math.max(-1, Math.min(1, inputData[offset]));
            pcmData[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
          }
        }

        wsRef.current.send(pcmData.buffer);
      };

      analyserRef.current.connect(processorRef.current);
      processorRef.current.connect(audioContextRef.current.destination);

      setIsRecording(true);
      // Start volume update loop
      requestAnimationFrame(updateVolume);
    } catch (err) {
      console.error('Error accessing microphone:', err);
      setError('Error accessing microphone: ' + err.message);
    }
  };

  const updateVolume = () => {
    if (
      !analyserRef.current ||
      !wsRef.current ||
      wsRef.current.readyState !== WebSocket.OPEN
    )
      return;

    const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
    analyserRef.current.getByteFrequencyData(dataArray);

    // Calculate average volume
    let sum = 0;
    for (let i = 0; i < dataArray.length; i++) {
      sum += dataArray[i];
    }
    const avg = sum / dataArray.length;

    // Scale down for visualizer
    setVolume(avg);

    animationFrameRef.current = requestAnimationFrame(updateVolume);
  };

  const stopRecording = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    // Do not stop videoRef here, it is managed by useEffect
    if (sourceRef.current) {
      sourceRef.current.disconnect();
    }
    if (processorRef.current) {
      processorRef.current.disconnect();
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    outputNodeRef.current = null;
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
    setIsRecording(false);
    isPlayingRef.current = false;
    audioQueueRef.current = [];
    nextStartTimeRef.current = 0;
    setVolume(0);
  };

  const clearAudioQueue = () => {
    activeSourcesRef.current.forEach(source => {
      try {
        source.stop();
      } catch (e) {
        // ignore
      }
    });
    activeSourcesRef.current = [];
    if (audioContextRef.current) {
      nextStartTimeRef.current = audioContextRef.current.currentTime;
    }
  };

  const toggleMonitoring = () => {
    const newState = !monitoringEnabled;
    setMonitoringEnabled(newState);

    // Clear guide message when disabling
    if (!newState) {
      setGuideMessage('');
      setCustomerInsights([]);
      setCurrentPhase('');
      setRecommendedProduct('');
      setProductSpecs([]);
      setPinnedProduct(null);
      setNewSuggestion(null);
    }

    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(
        JSON.stringify({
          type: 'toggle_monitoring',
          enabled: newState,
        }),
      );
    }
  };

  const handlePinProduct = () => {
    if (pinnedProduct) {
      // Unpin
      setPinnedProduct(null);
      setNewSuggestion(null);
    } else if (recommendedProduct) {
      // Pin current product
      setPinnedProduct({ name: recommendedProduct, specs: productSpecs });
      setNewSuggestion(null);
    }
  };

  const handleUpdatePinToNew = () => {
    if (newSuggestion) {
      setPinnedProduct({
        name: newSuggestion.name,
        specs: newSuggestion.specs,
      });
      setRecommendedProduct(newSuggestion.name);
      setProductSpecs(newSuggestion.specs);
      setNewSuggestion(null);
    }
  };

  // Determine what product to display
  const displayProduct =
    pinnedProduct ||
    (recommendedProduct
      ? { name: recommendedProduct, specs: productSpecs }
      : null);

  const handleAudioData = async arrayBuffer => {
    if (!audioContextRef.current) return;

    try {
      const playbackContext = audioContextRef.current;
      const sampleRate = 24000; // Gemini output rate
      const int16Data = new Int16Array(arrayBuffer);
      const float32Data = new Float32Array(int16Data.length);

      for (let i = 0; i < int16Data.length; i++) {
        float32Data[i] = int16Data[i] / 32768.0;
      }

      const audioBuffer = playbackContext.createBuffer(
        1,
        float32Data.length,
        sampleRate,
      );
      audioBuffer.copyToChannel(float32Data, 0);

      const source = playbackContext.createBufferSource();
      source.buffer = audioBuffer;

      if (outputNodeRef.current) {
        source.connect(outputNodeRef.current);
      } else {
        source.connect(playbackContext.destination);
      }

      // Schedule playback
      const currentTime = playbackContext.currentTime;
      if (nextStartTimeRef.current < currentTime) {
        nextStartTimeRef.current = currentTime;
      }

      source.onended = () => {
        activeSourcesRef.current = activeSourcesRef.current.filter(
          s => s !== source,
        );
      };
      activeSourcesRef.current.push(source);

      source.start(nextStartTimeRef.current);
      nextStartTimeRef.current += audioBuffer.duration;
    } catch (e) {
      console.error('Error playing audio', e);
    }
  };

  const playNextInQueue = async () => {
    // Deprecated in favor of direct scheduling in handleAudioData
  };

  return (
    <div
      style={{
        position: 'relative',
        width: '100vw',
        height: 'calc(100vh - 50px)',
        overflow: 'hidden',
        backgroundColor: '#111827',
      }}
    >
      {/* Background Layer - Visualizer */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 0,
        }}
      >
        <AudioOrbVisual
          inputNode={sourceRef.current}
          outputNode={outputNodeRef.current}
        />
      </div>

      {/* Content Layer - UI Elements */}
      <div
        style={{
          position: 'relative',
          zIndex: 10,
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          pointerEvents: 'none',
          overflowY:'auto',
        }}
      >
        {/* Top Section: Header */}
        <div
          style={{
            padding: '2rem',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            pointerEvents: 'auto',
            background:
              'linear-gradient(to bottom, rgba(0,0,0,0.8), transparent)',
          }}
        >
          <Header as="h2" style={{ color: 'white', marginBottom: '1rem' }}>
            {currentPersona
              ? `${currentPersona.name} (${currentPersona.gender === 'MALE' ? '남' : '여'}, ${currentPersona.persona.age.value})`
              : '고객 응대 시뮬레이션'}
          </Header>

          {error && (
            <Message negative style={{ marginBottom: '1rem' }}>
              <Message.Header>Error</Message.Header>
              <p>{error}</p>
            </Message>
          )}

          <Button
            toggle
            active={monitoringEnabled}
            onClick={toggleMonitoring}
            color={monitoringEnabled ? 'yellow' : 'grey'}
            size="small"
            compact
            style={{ marginTop: '0.5rem', opacity: 0.9 }}
          >
            <Icon name="lightbulb" />
            {monitoringEnabled ? 'AI 코칭 켜짐' : 'AI 코칭 꺼짐'}
          </Button>
        </div>

        {/* Middle Section: Transcripts (Push to bottom) */}
        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            padding: '2rem',
            pointerEvents: 'none',
            paddingBottom: '180px',
          }}
        >
          <div
            style={{
              width: '100%',
              maxWidth: '800px',
              margin: '0 auto',
              pointerEvents: 'auto',
            }}
          >
            {transcript && (
              <Segment
                inverted
                style={{
                  backgroundColor: 'rgba(31, 41, 55, 0.8)',
                  marginBottom: '1rem',
                  backdropFilter: 'blur(4px)',
                }}
              >
                <div
                  style={{
                    fontSize: '0.75rem',
                    color: '#9CA3AF',
                    textTransform: 'uppercase',
                    marginBottom: '0.25rem',
                  }}
                >
                  You
                </div>
                <div style={{ color: 'white', fontSize: '1.1rem' }}>
                  {transcript}
                </div>
              </Segment>
            )}
            {aiResponse && (
              <Segment
                inverted
                style={{
                  backgroundColor: 'rgba(30, 58, 138, 0.6)',
                  marginBottom: '1rem',
                  backdropFilter: 'blur(4px)',
                }}
              >
                <div
                  style={{
                    fontSize: '0.75rem',
                    color: '#60A5FA',
                    textTransform: 'uppercase',
                    marginBottom: '0.25rem',
                  }}
                >
                  {currentPersona ? currentPersona.name : 'AI'}
                </div>
                <div style={{ color: 'white', fontSize: '1.1rem' }}>
                  {aiResponse}
                </div>
              </Segment>
            )}
            <AICoachingPanel
              guideMessage={guideMessage}
              currentPhase={currentPhase}
              customerInsights={customerInsights}
              displayProduct={displayProduct}
              pinnedProduct={pinnedProduct}
              newSuggestion={newSuggestion}
              handlePinProduct={handlePinProduct}
              handleUpdatePinToNew={handleUpdatePinToNew}
            />
          </div>
        </div>
      </div>
      {/* Bottom Section: Controls */}
      <div
        style={{
          position: 'absolute',
          bottom: isMobile ? '20px' : '60px',
          left: 0,
          width: '100%',
          display: 'flex',
          justifyContent: isMobile ? 'flex-start' : 'center',
          pointerEvents: 'auto',
          paddingLeft: isMobile ? '20px' : 0,
        }}
      >
        {!isConnected ? (
          isMobile ? (
            <Button
              color="green"
              size="large"
              onClick={connect}
              icon
              circular
              style={{
                boxShadow: '0 4px 14px 0 rgba(0,0,0,0.39)',
                width: '45px',
                height: '45px',
                minWidth: '45px',
                minHeight: '45px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 0,
              }}
            >
              <Icon
                name="play"
                style={{
                  margin: 0,
                  fontSize: '18px',
                  transform: 'translateX(1px)',
                }}
              />
            </Button>
          ) : (
            <Button
              color="green"
              size="huge"
              onClick={connect}
              icon
              labelPosition="left"
              circular
              style={{ boxShadow: '0 4px 14px 0 rgba(0,0,0,0.39)' }}
            >
              <Icon name="play" />
              세션 시작
            </Button>
          )
        ) : isMobile ? (
          <Button
            color="red"
            size="large"
            onClick={disconnect}
            icon
            circular
            style={{
              boxShadow: '0 4px 14px 0 rgba(0,0,0,0.39)',
              width: '45px',
              height: '45px',
              minWidth: '45px',
              minHeight: '45px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0,
            }}
          >
            <Icon
              name="stop"
              style={{
                margin: 0,
                fontSize: '16px',
                transform: 'translateX(-2px) translateY(-1px)',
              }}
            />
          </Button>
        ) : (
          <Button
            color="red"
            size="huge"
            onClick={disconnect}
            icon
            labelPosition="left"
            circular
            style={{ boxShadow: '0 4px 14px 0 rgba(0,0,0,0.39)' }}
          >
            <Icon name="stop" />
            세션 종료
          </Button>
        )}
      </div>
      <CameraFeed videoRef={videoRef} isRecording={isRecording} />
    </div>
  );
};

export default AudioOrb;
